/**
 * ProgressTrackerAuditLogModal - Searchable audit log for progress tracker changes.
 *
 * Shown via the "Audit Log" button on top of each discipline matrix dashboard.
 * Features:
 *  - Surfaces both successful changes AND failures (status = error).
 *  - Includes previously-recorded history (piping progress trends) so changes made
 *    before the audit log existed still appear.
 *  - Shows field-level "what changed" (old -> new) detail.
 *  - Click an entry to jump to / highlight the changed row in the matrix.
 *  - Superadmin-only "Revert" button to restore a change.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const prettyField = (field) => {
  if (!field) return '';
  return String(field)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bUdf\b/i, 'UDF');
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const ProgressTrackerAuditLogModal = ({
  isOpen, onClose, projectId, discipline, disciplineLabel,
  onNavigateToRow, onReverted,
}) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = all, 'error', 'success'
  const [page, setPage] = useState(0);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const LIMIT = 50;

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
      if (discipline) params.set('discipline', discipline);
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);

      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/progress-tracker-audit-log/project/${projectId}?${params.toString()}`
      );
      if (response.data.success) {
        setEntries(response.data.data.rows || []);
        setTotalRows(response.data.data.totalRows || 0);
      }
    } catch (error) {
      console.error('Error fetching progress tracker audit log:', error);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId, discipline, search, statusFilter, page]);

  useEffect(() => {
    if (isOpen) fetchEntries();
  }, [isOpen, fetchEntries]);

  // Determine superadmin (controls revert visibility) - mirrors ProjectSidebar pattern
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (!cancelled && resp.data.success) {
          setIsSuperadmin(!!resp.data.data?.isSuperadmin);
        }
      } catch (e) {
        if (!cancelled) setIsSuperadmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, instance, accounts]);

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      import: { bg: '#dbeafe', color: '#1e40af', label: 'Import' },
      edit: { bg: '#fef3c7', color: '#92400e', label: 'Edit' },
      create: { bg: '#d1fae5', color: '#065f46', label: 'Create' },
      update: { bg: '#fef3c7', color: '#92400e', label: 'Update' },
      add: { bg: '#d1fae5', color: '#065f46', label: 'Add' },
      delete: { bg: '#fee2e2', color: '#991b1b', label: 'Delete' },
      move: { bg: '#ede9fe', color: '#5b21b6', label: 'Move' },
      revert: { bg: '#e0e7ff', color: '#3730a3', label: 'Revert' },
    };
    const s = styles[action] || { bg: '#f3f4f6', color: '#374151', label: action };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
        backgroundColor: s.bg, color: s.color, textTransform: 'uppercase',
      }}>
        {s.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const isError = status === 'error';
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
        backgroundColor: isError ? '#fee2e2' : '#dcfce7',
        color: isError ? '#991b1b' : '#166534', textTransform: 'uppercase',
      }}>
        {isError ? 'Error' : 'OK'}
      </span>
    );
  };

  const parseDetails = (details) => {
    if (!details) return {};
    if (typeof details === 'string') {
      try { return JSON.parse(details); } catch { return {}; }
    }
    return details;
  };

  // Rich "what changed" rendering
  const renderWhatChanged = (entry) => {
    if (entry.status === 'error' && entry.error_message) {
      return <span style={{ color: '#b91c1c' }}>{entry.error_message}</span>;
    }
    const details = parseDetails(entry.details);
    const fieldChanges = Array.isArray(details.fieldChanges) ? details.fieldChanges : [];
    const isoLabel = details.isoSheetNumber || details.lineNumber;

    // Inferred deletion of a row that no longer exists live
    if (details.inferredDelete) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px' }}>
          <span style={{ color: '#991b1b', fontWeight: 600 }}>
            Row deleted{isoLabel ? `: ${isoLabel}` : ''}
          </span>
          <span style={{ color: '#9ca3af' }}>
            deletion not directly recorded (predates audit log){details.lastEditedBy ? ` · last edited by ${String(details.lastEditedBy).split('@')[0]}` : ''}
          </span>
        </div>
      );
    }

    // Row creation ("who added")
    if (details.created) {
      return (
        <span style={{ fontSize: '12px' }}>
          <span style={{ color: '#065f46', fontWeight: 600 }}>Row created</span>
          {isoLabel ? <span style={{ color: '#374151' }}>{`: ${isoLabel}`}</span> : null}
        </span>
      );
    }

    if (fieldChanges.length > 0) {
      const shown = fieldChanges.slice(0, 6);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {shown.map((fc, i) => (
            <div key={i} style={{ fontSize: '12px' }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>{prettyField(fc.field)}</span>
              {': '}
              <span style={{ color: '#9ca3af', textDecoration: 'line-through' }}>{formatVal(fc.from)}</span>
              <span style={{ color: '#6b7280' }}>{' \u2192 '}</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{formatVal(fc.to)}</span>
            </div>
          ))}
          {fieldChanges.length > shown.length && (
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>+{fieldChanges.length - shown.length} more field(s)</div>
          )}
        </div>
      );
    }

    // Fallbacks: summarize known detail fields
    const summary = [];
    if (details.deletedCount != null) summary.push(`deleted ${details.deletedCount} row(s)`);
    if (details.insertedCount != null) summary.push(`imported ${details.insertedCount} row(s)`);
    if (details.rowsReverted != null) summary.push(`reverted ${details.rowsReverted} row(s)`);
    if (details.queued) summary.push(`queued (job ${details.jobId})`);
    if (details.errorCode) summary.push(details.errorCode);
    if (summary.length) return <span style={{ color: '#6b7280' }}>{summary.join(', ')}</span>;
    return <span style={{ color: '#9ca3af' }}>—</span>;
  };

  const isRevertable = (entry) =>
    isSuperadmin &&
    entry.source !== 'history' &&
    ['edit', 'update'].includes(entry.action) &&
    entry.status === 'success' &&
    Array.isArray(parseDetails(entry.details).fieldChanges) &&
    parseDetails(entry.details).fieldChanges.length > 0;

  const handleRevert = async (entry) => {
    const fc = parseDetails(entry.details).fieldChanges || [];
    const summary = fc.slice(0, 5).map(c => `${prettyField(c.field)} -> ${formatVal(c.from)}`).join('\n');
    if (!window.confirm(`Revert this change?\n\nThe following will be restored:\n${summary}${fc.length > 5 ? `\n+${fc.length - 5} more` : ''}`)) {
      return;
    }
    try {
      setRevertingId(entry.id);
      const resp = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/progress-tracker-audit-log/${entry.id}/revert`
      );
      if (resp.data.success) {
        await fetchEntries();
        if (onReverted) onReverted();
      } else {
        window.alert(resp.data.message || 'Revert failed');
      }
    } catch (error) {
      window.alert(error.response?.data?.message || 'Revert failed');
    } finally {
      setRevertingId(null);
    }
  };

  const handleNavigate = (entry) => {
    const firstId = (entry.entity_id || '').split(',')[0]?.trim();
    if (!firstId || !onNavigateToRow) return;
    // Pass the first changed field so the matrix can scroll to the exact column.
    const details = parseDetails(entry.details);
    const firstChange = Array.isArray(details.fieldChanges) ? details.fieldChanges[0] : null;
    const field = firstChange?.field || null;
    onNavigateToRow(firstId, field);
  };

  const totalPages = Math.ceil(totalRows / LIMIT);
  const title = disciplineLabel ? `${disciplineLabel} Audit Log` : 'Progress Tracker Audit Log';

  const filterBtn = (value, label) => (
    <button
      onClick={() => setStatusFilter(value)}
      style={{
        padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
        border: '1px solid ' + (statusFilter === value ? '#4f46e5' : '#d1d5db'),
        backgroundColor: statusFilter === value ? '#4f46e5' : '#fff',
        color: statusFilter === value ? '#fff' : '#374151', fontWeight: '500',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '1000px', maxWidth: '95vw', maxHeight: '85vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>{title}</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              {totalRows} total entries · click a row to jump to it
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer',
          }}>&times;</button>
        </div>

        {/* Search + filters */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              placeholder="Search action, user, field, error, value..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px 8px 36px',
                borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px',
              }}
            />
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>
              🔍
            </span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {filterBtn('', 'All')}
            {filterBtn('error', 'Errors')}
            {filterBtn('success', 'OK')}
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 240px)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {search || statusFilter ? 'No results matching your filters.' : 'No audit log entries yet.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>User</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Row</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>What changed</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr
                    key={entry.id}
                    onClick={() => handleNavigate(entry)}
                    title={entry.entity_id ? 'Click to jump to this row in the matrix' : ''}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: entry.status === 'error' ? '#fef2f2' : 'transparent',
                      cursor: entry.entity_id ? 'pointer' : 'default',
                    }}
                  >
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {formatDate(entry.created_at)}
                      {entry.source === 'history' && (
                        <span style={{ marginLeft: 6, fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' }}>history</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {entry.user_email?.split('@')[0] || 'system'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {getActionBadge(entry.action)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {getStatusBadge(entry.status)}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#4f46e5', fontWeight: 600, maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.entity_id ? `#${entry.entity_id}` : ''}
                    </td>
                    <td style={{ padding: '8px 12px', maxWidth: '380px' }}>
                      {renderWhatChanged(entry)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isRevertable(entry) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRevert(entry); }}
                          disabled={revertingId === entry.id}
                          style={{
                            padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            border: '1px solid #c7d2fe', borderRadius: '6px',
                            backgroundColor: revertingId === entry.id ? '#eef2ff' : '#fff', color: '#4338ca',
                          }}
                          title="Restore this row's previous values (superadmin only)"
                        >
                          {revertingId === entry.id ? 'Reverting…' : '↩ Revert'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '12px 24px', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center',
          }}>
            <button
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              style={{
                padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '4px',
                cursor: page === 0 ? 'default' : 'pointer', fontSize: '13px',
                backgroundColor: page === 0 ? '#f3f4f6' : '#fff',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              style={{
                padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '4px',
                cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontSize: '13px',
                backgroundColor: page >= totalPages - 1 ? '#f3f4f6' : '#fff',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressTrackerAuditLogModal;
